import { Calendar, FolderOpen, Mail, Mic, type LucideIcon } from 'lucide-react';
import { INTEGRATION_PROVIDERS, type IntegrationProvider } from '@repo/shared-types';
import type { HubSource, SetDestinationsInput } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { HubSourceRow } from './HubSourceRow';

const ICONS: Partial<Record<IntegrationProvider, LucideIcon>> = {
  GRANOLA: Mic,
  GOOGLE_MAIL: Mail,
  GOOGLE_DRIVE: FolderOpen,
  GOOGLE_CALENDAR: Calendar,
};

const BLURBS: Partial<Record<IntegrationProvider, string>> = {
  GRANOLA: 'Meeting notes, one row per connected account or folder.',
  GOOGLE_MAIL: 'Mailboxes and labels. Attachments are filed as documents.',
  GOOGLE_DRIVE: 'Shared Drives and folders.',
  GOOGLE_CALENDAR: 'Meetings that automation triggers fire on. Never filed as memory.',
};

/** Every hub source of one provider, grouped by the hub connection it comes from. */
export function ProviderSection({
  provider,
  sources,
  consoleUrl,
  busyKey,
  onSave,
  pinTo,
}: {
  provider: IntegrationProvider;
  sources: HubSource[];
  consoleUrl: string | null;
  busyKey: string | null;
  onSave: (source: HubSource, input: Omit<SetDestinationsInput, 'provider' | 'externalId'>) => Promise<void>;
  pinTo?: { id: string; name: string };
}) {
  const definition = INTEGRATION_PROVIDERS.find((p) => p.provider === provider)!;
  const Icon = ICONS[provider] ?? Mic;
  const groups = new Map<string, HubSource[]>();
  for (const source of sources) {
    const account = source.account ?? '';
    groups.set(account, [...(groups.get(account) ?? []), source]);
  }

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <Icon size={16} className={sources.some((s) => s.organization || s.projects.length) ? 'text-orange-500' : 'text-ink-faint'} />
        <CardTitle>{definition.label}</CardTitle>
        <span className="text-xs text-ink-faint">{BLURBS[provider]}</span>
      </CardHeader>
      <CardContent className="p-0">
        {sources.length === 0 ? (
          <p className="px-6 py-4 text-sm text-ink-faint">
            The hub isn't collecting any {definition.sourceNounPlural} yet.{' '}
            {consoleUrl && (
              <a href={consoleUrl} target="_blank" rel="noreferrer" className="text-orange-500 hover:text-orange-600 underline">
                Add one in the hub console
              </a>
            )}
          </p>
        ) : (
          [...groups].map(([account, rows]) => (
            <div key={account || '-'} className="border-t border-border-subtle first:border-t-0">
              {account && (
                <p className="px-4 pt-3 text-[11px] font-medium uppercase tracking-wider text-ink-faint">{account}</p>
              )}
              <ul className="divide-y divide-border-subtle">
                {rows.map((source) => (
                  <HubSourceRow
                    key={source.externalId}
                    source={source}
                    busy={busyKey === `${source.provider}:${source.externalId}`}
                    pinTo={pinTo}
                    onSave={(input) => onSave(source, input)}
                  />
                ))}
              </ul>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
